import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BinariesService } from '@services/binaries/binaries.service';
import { ConfigService } from '@services/config.service';
import { NotificationService } from '@services/notification.service';
import { BinaryLiteWithWarning } from '@shared/models/binaries/Binary-lite-with-warning.model';
import { ApplianceGroup, DataRefsByKey } from '@shared/models/dataref.model';
import { OfsException } from '@shared/models/error/ofs-exception.model';
import { RecipeLite } from '@shared/models/recipe/recipe-lite.model';
import { StepLite } from '@shared/models/recipe/step-lite.model';

@Component({
  selector: 'app-generate-binary-form',
  templateUrl: './generate-binary-form.component.html',
  styleUrls: ['./generate-binary-form.component.scss'],
  standalone: false,
})
export class GenerateBinaryFormComponent implements OnInit {
  // Input data - see constructor
  @Input() recipeOriginal: RecipeLite = {} as RecipeLite;
  @Input() variants: RecipeLite[] = [];
  @Input() applianceGroupsRefData: DataRefsByKey<ApplianceGroup> = {};
  @Input() recipeDirty = true;

  @Output() newBinaryHasBeenGenerated$: EventEmitter<void> = new EventEmitter<void>(true);

  // Working variables
  eligibleApplianceGroups?: { key: string; label: string }[];
  generateFormErrorMessage = '';

  readonly types = [
    { key: 'USB', label: 'USB' },
    { key: 'CONNECT', label: 'Connect' },
  ];

  readonly formGroupGenerate: UntypedFormGroup = new UntypedFormGroup({
    applianceGroup: new UntypedFormControl(undefined, Validators.required),
    type: new UntypedFormControl(this.types[0].key, Validators.required),
  });

  constructor(
    private readonly binariesService: BinariesService,
    private readonly configService: ConfigService,
    private readonly notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.eligibleApplianceGroups = (this.binariesService.buildApplianceGroupsCandidates(this.variants) ?? []).map(
      (applianceGroupKey: string) => ({
        key: applianceGroupKey,
        label: this.applianceGroupsRefData[applianceGroupKey]?.translatedName ?? applianceGroupKey,
      }),
    );

    const applianceGroupFormControl = this.formGroupGenerate.get('applianceGroup');

    if (this.eligibleApplianceGroups?.length) {
      applianceGroupFormControl?.setValue(this.eligibleApplianceGroups[0].key);
    }
  }

  shouldDisableGeneration(): boolean {
    this.generateFormErrorMessage = '';

    if (!this.recipeOriginal) {
      return true;
    }

    if (this.recipeDirty) {
      this.generateFormErrorMessage = 'BINARIES.MSG.ERROR.HAVE_TO_SAVE';
      return true;
    }

    if (!this.eligibleApplianceGroups?.length || this.formGroupGenerate.invalid) {
      this.generateFormErrorMessage = 'BINARIES.MSG.ERROR.VARIANTS';
      return true;
    }

    const allowedCourseTypes = this.configService.getAppConfig()?.courseTypesForBinaries ?? [];

    if (!this.recipeOriginal?.courses?.some((course) => allowedCourseTypes.includes(course))) {
      this.generateFormErrorMessage = 'BINARIES.MSG.ERROR.COURSE_TYPE';
      return true;
    }

    if (this.recipeOriginal?.steps?.some((step: StepLite) => step?.type === 'KEEP_WARM' && !step?.applianceDescription)) {
      this.generateFormErrorMessage = 'BINARIES.MSG.ERROR.STEP_EMPTY_APPLIANCEDESCRIPTION';
      return true;
    }

    return false;
  }

  generate(): void {
    if (this.shouldDisableGeneration()) {
      return undefined;
    }

    //Handle the correct identifier for v1/variants
    this.recipeOriginal.identifier.functionalId = this.recipeOriginal.identifier.functionalId.replace(/\//g, '%2F');

    this.binariesService
      .generateForRecipe({
        recipeId: this.recipeOriginal.identifier,
        groupingId: this.recipeOriginal.groupingId,
        ...this.formGroupGenerate.value,
      })
      .subscribe(
        (res: BinaryLiteWithWarning) => {
          this.notificationService.sendSuccess('BINARIES.GENERATED');
          this.handleGenerationWarnings(res);
          this.newBinaryHasBeenGenerated$.emit();
        },
        (err) => {
          this.binariesService.handleBineratorErrors(err);
        },
      );
  }

  handleGenerationWarnings(res: BinaryLiteWithWarning): void {
    if (!res?.invalidInputExceptionBus?.length) {
      return;
    }

    res.invalidInputExceptionBus.forEach((warning: OfsException) => {
      const interpolateParams: { [p: string]: string } = {};

      if (warning.placeHolders?.length) {
        warning.placeHolders.forEach(
          (placeHolder: { key: string | number; values: string[] }) => (interpolateParams[placeHolder.key] = placeHolder.values[0]),
        );
      }

      const applianceGroupKey: string = interpolateParams.applianceGroup;

      if (applianceGroupKey && this.applianceGroupsRefData[applianceGroupKey]?.label) {
        interpolateParams.applianceGroup = this.applianceGroupsRefData[applianceGroupKey].label;
      }

      this.notificationService.sendWarning('WARNING_BINARIES_NO_APPLIANCE_GROUP_BODY', interpolateParams);
    });
  }
}
